import React, {
  useRef, useState, useCallback, useEffect,
  KeyboardEvent, ClipboardEvent
} from 'react';

import { motion } from 'framer-motion';

import Input from '@components/common/Input/Input';

interface PinInputProps {
  length?: number;
  onChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  error?: string;
}

const usePinInput = (length: number, onChange?: (value: string) => void) => {
  const [values, setValues] = useState<string[]>([]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    setValues(Array(length).fill(''));
  }, [length]);

  const focusInput = useCallback((index: number) => {
    inputRefs.current[index]?.focus();
  }, []);

  const updateValues = useCallback((newValues: string[]) => {
    setValues(newValues);
    onChange?.(newValues.join(''));
  }, [onChange]);

  const handleChange = useCallback((index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    // Handle multiple digits input (iOS autofill case)
    if (index === 0 && value.length > 1) {
      const newValues = Array(length).fill('');
      value.split('').forEach((char, i) => {
        if (i < length) newValues[i] = char;
      });
      updateValues(newValues);
      focusInput(Math.min(value.length, length - 1));
      return;
    }

    // Handle single digit input
    const newValues = [...values];
    newValues[index] = value;
    updateValues(newValues);

    if (value && index < length - 1) {
      focusInput(index + 1);
    }
  }, [length, values, updateValues, focusInput]);

  const handleKeyDown = useCallback((index: number, e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Backspace':
        if (!values[index] && index > 0) focusInput(index - 1);
        break;
      case 'ArrowLeft':
        if (index > 0) focusInput(index - 1);
        break;
      case 'ArrowRight':
        if (index < length - 1) focusInput(index + 1);
        break;
    }
  }, [values, length, focusInput]);

  const handlePaste = useCallback((e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').slice(0, length);

    if (!/^\d+$/.test(pastedData)) return;

    const newValues = [...values];
    pastedData.split('').forEach((char, i) => {
      if (i < length) newValues[i] = char;
    });

    updateValues(newValues);
    focusInput(Math.min(pastedData.length, length - 1));
  }, [length, values, updateValues, focusInput]);

  return {
    values,
    inputRefs,
    handleChange,
    handleKeyDown,
    handlePaste
  };
};

const PinInput: React.FC<PinInputProps> = ({
  length = 6,
  onChange,
  disabled = false,
  className = '',
  error
}) => {
  const {
    values,
    inputRefs,
    handleChange,
    handleKeyDown,
    handlePaste
  } = usePinInput(length, onChange);

  return (
    <div className="relative">
      <motion.div
        className={`flex flex-row gap-2 ${className}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.3,
          ease: "easeInOut"
        }}
      >
        {Array.from({ length }).map((_, index) => (
          <Input
            key={index}
            ref={el => { inputRefs.current[index] = el }}
            className="w-12 text-center"
            type="text"
            inputMode="numeric"
            maxLength={index === 0 ? length : 1}
            value={values[index] || ''}
            disabled={disabled}
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onPaste={handlePaste}
            placeholder="0"
            autoComplete={index === 0 ? "one-time-code" : "off"}
          />
        ))}
      </motion.div>
      {error && (
        <motion.div
          className="pt-2 w-full"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{
            duration: 0.2,
            ease: "easeOut"
          }}
        >
          <span className="text-sm text-red-500">{error}</span>
        </motion.div>
      )}
    </div>
  );
};

export default PinInput;